<script>
import EmptyStateBlock from '@/components/general/empty-state-block.vue'
import { Weekdays } from '@/shared/constants/date/DayOfWeek'
import { AvailabilityState } from '@/shared/constants/candidate/CandidateAvailability'
import calendarMonthLayout from '@/utils/calendar-month-layout'

import CandidateAvailabilityItem from './candidate-availability-item.vue'

export default {
  name: 'CandidateAvailabilityCalendar',
  components: {
    CandidateAvailabilityItem,
    EmptyStateBlock,
  },
  props: {
    selectedMonth: {
      type: Object,
      required: true,
    },
    availability: {
      type: Array,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    transitionName: {
      type: String,
      required: true,
    },
  },
  computed: {
    daysOfWeek() {
      const { Header } = AvailabilityState

      return Weekdays.map(({ key, letter }) => ({
        key,
        state: Header,
        label: letter,
      }))
    },
    candidateAvailability() {
      const cells =
        this.availability || calendarMonthLayout(this.selectedMonth.date)

      return cells.map((cell) => ({
        ...cell,
        state: cell.isoDate ? cell.state : AvailabilityState.Empty,
        isLoading: this.isLoading && cell.isoDate !== null,
      }))
    },
  },
}
</script>

<template>
  <div>
    <div class="availability-grid mb-2">
      <CandidateAvailabilityItem
        v-for="day in daysOfWeek"
        :key="day.key"
        v-bind="day"
      />
    </div>

    <transition
      :name="transitionName"
      mode="out-in"
      @after-enter="$emit('monthTransitionComplete')"
    >
      <EmptyStateBlock v-if="hasError" key="error">
        <template v-slot:icon>
          <v-icon size="40" color="red lighten-1"
            >mdi-alert-circle-outline</v-icon
          >
        </template>
        <template v-slot:title>
          <h3 class="my-2">{{
            $t('candidateDetailsPopup.availability.error.title')
          }}</h3>
        </template>
        <template v-slot:description>
          <p>{{ $t('candidateDetailsPopup.availability.error.body') }}</p>
        </template>
        <template v-slot:button>
          <v-btn
            data-testid="candidate-availability-calendar-retry"
            color="grey darken-1"
            depressed
            outlined
            class="mt-2"
            @click="$emit('retry')"
          >
            {{ $t('general.retry') }}
          </v-btn>
        </template>
      </EmptyStateBlock>

      <div
        v-else
        :key="`month-${selectedMonth.value}`"
        class="availability-grid"
      >
        <CandidateAvailabilityItem
          v-for="day in candidateAvailability"
          :key="day.key"
          v-bind="day"
        />
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/spacing';
@import '@/styles/_vue-transitions.scss';

.fade-up-enter,
.fade-up-leave-to {
  transform: translateY(-4px);
}

.availability-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: $spacing-2xs;
}
</style>
